/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Board Finder 2026 - Data classifier
 * Maps Exocet windsurf boards to wizard discipline/level/volume profiles.
 *
 * Source-of-truth priority for each board:
 *   1. shopify.recommended-skill-level   (when populated, official taxonomy)
 *   2. shopify.windsurfing-board-style   (when populated, official taxonomy)
 *   3. MODEL_PROFILES lookup by family extracted from product title (fallback)
 *   4. If family unknown -> excluded from pool (warning logged)
 *
 * Backfill of the Shopify taxonomy metafields is a separate sprint (Andrew/Job).
 * Until then this client-side classifier keeps the wizard functional.
 */

(function () {
  "use strict";

  const LEVEL_ORDER = ["beginner", "intermediate", "advanced", "expert"];

  // Wizard discipline values (lowercase) <-- card data-value attributes
  // freeride, slalom, wave, freestyle, foil, race

  const DISCIPLINE_MAP = {
    Freewave: "freeride",
    Freeride: "freeride",
    Wave: "wave",
    Slalom: "slalom",
    Race: "race",
    Raceboard: "race",
    Freestyle: "freestyle",
    Foil: "foil",
    Course: "race",
  };

  const SKILL_MAP = {
    Beginner: "beginner",
    Intermediate: "intermediate",
    Advanced: "advanced",
    Expert: "expert",
  };

  // Per-family defaults. Volume ranges are observed (Cross V8: 74-124L) or inferred.
  // Reviewable: edit this table to tune recommendations before merge.
  const MODEL_PROFILES = {
    "Cross V8": { disciplines: ["freeride"], levels: ["intermediate", "advanced"], volRange: [74, 124] },
    "RS V5": { disciplines: ["race", "slalom"], levels: ["advanced", "expert"], volRange: [80, 110] },
    "RS V6": { disciplines: ["race", "slalom"], levels: ["advanced", "expert"], volRange: [80, 110] },
    "RS V7": { disciplines: ["race", "slalom"], levels: ["advanced", "expert"], volRange: [80, 110] },
    "Nano V3": { disciplines: ["freeride", "wave"], levels: ["intermediate", "advanced"], volRange: [135, 155] },
    "Nano V4": { disciplines: ["wave"], levels: ["advanced", "expert"], volRange: [85, 110] },
    "Nano V5": { disciplines: ["wave", "freeride"], levels: ["intermediate", "advanced"], volRange: [130, 160] },
    "Xwave V3": { disciplines: ["wave"], levels: ["advanced", "expert"], volRange: [76, 106] },
    "Breeze V1": { disciplines: ["wave", "freeride"], levels: ["beginner", "intermediate"], volRange: [125, 160] },
    "Breeze V2": { disciplines: ["wave", "freeride"], levels: ["beginner", "intermediate"], volRange: [125, 160] },
    "Link V4": { disciplines: ["freeride"], levels: ["beginner"], volRange: [140, 230] },
    "Link V5": { disciplines: ["freeride"], levels: ["beginner"], volRange: [140, 230] },
    "Neon HP": { disciplines: ["wave", "freeride"], levels: ["intermediate"], volRange: [110, 140] },
    "Neon SL": { disciplines: ["freeride"], levels: ["beginner"], volRange: [150, 220] },
    "Windsurfer LT": { disciplines: ["freeride"], levels: ["beginner"], volRange: [150, 220] },
    Zypher: { disciplines: ["freeride"], levels: ["beginner"], volRange: [150, 220] },
    "WindSup V4": { disciplines: ["freeride"], levels: ["beginner"], volRange: [180, 280] },
    "Scross V3": { disciplines: ["freeride"], levels: ["intermediate", "advanced"], volRange: [100, 130] },
    Maverick: { disciplines: ["freeride"], levels: ["intermediate"], volRange: [110, 140] },
    "Maverick II": { disciplines: ["freeride"], levels: ["intermediate"], volRange: [110, 140] },
    "RF Foil V3": { disciplines: ["foil"], levels: ["intermediate", "advanced"], volRange: [81, 91] },
    "RF Foil V4": { disciplines: ["foil"], levels: ["advanced", "expert"], volRange: [75, 85] },
    "RS V6 380": { disciplines: ["race", "slalom"], levels: ["advanced", "expert"], volRange: [80, 110] },
    "RS V7 380": { disciplines: ["race", "slalom"], levels: ["advanced", "expert"], volRange: [80, 110] },
    "Freefoil V3": { disciplines: ["foil"], levels: ["intermediate", "advanced"], volRange: [90, 130] },
  };

  // Ordered patterns -- longest first to avoid partial matches (e.g. "RS V" before "RS")
  const FAMILY_PATTERNS = [
    /^(RS V7 380)/,
    /^(RS V6 380)/,
    /^(Freefoil V\d+)/,
    /^(WindSup V\d+)/,
    /^(RF Foil V\d+)/,
    /^(Windsurfer LT)/,
    /^(Maverick II)/,
    /^(Scross V\d+)/,
    /^(Xwave V\d+)/,
    /^(Breeze V\d+)/,
    /^(Cross V\d+)/,
    /^(Nano V\d+)/,
    /^(Link V\d+)/,
    /^(Neon (?:SL|HP))/,
    /^(RS V\d+)/,
    /^(Zypher)/,
    /^(Maverick)/,
  ];

  function extractFamilyFromTitle(title) {
    if (!title) return "";
    for (const pattern of FAMILY_PATTERNS) {
      const match = title.match(pattern);
      if (match) return match[1];
    }
    return "";
  }

  function extractVolumeFromTitle(title) {
    if (!title) return null;
    // Match standalone 2-3 digit number, optionally followed by L
    // e.g. "Cross V8 Carbon 104" -> 104, "Breeze V1 Silver 10'10" -> null (foot/inch not L)
    const match = title.match(/\s(\d{2,3})(?:L|\s|$)/i);
    if (match) {
      const vol = parseInt(match[1], 10);
      if (vol >= 50 && vol <= 350) return vol; // sanity range
    }
    return null;
  }

  /**
   * Classify a raw product (as injected by the Liquid section) into the wizard's
   * filtering schema. Pure function -- safe to call repeatedly.
   *
   * @param {Object} product  Raw product JSON from bf-products script
   * @returns {{ disciplines: string[], levels: string[], family: string, volumeNum: number|null }}
   */
  function classifyProduct(product) {
    const family = extractFamilyFromTitle(product.title) || product.family || "";
    const profile = MODEL_PROFILES[family];

    // Disciplines: prefer Shopify standard taxonomy, fall back to family profile
    let disciplines = [];
    if (Array.isArray(product.styleLabels) && product.styleLabels.length) {
      disciplines = product.styleLabels.map((label) => DISCIPLINE_MAP[label]).filter(Boolean);
    }
    if (!disciplines.length && profile) {
      disciplines = profile.disciplines.slice();
    }

    // Skill levels: prefer Shopify standard taxonomy, fall back to family profile
    let levels = [];
    if (Array.isArray(product.skillLabels) && product.skillLabels.length) {
      levels = product.skillLabels.map((label) => SKILL_MAP[label]).filter(Boolean);
    }
    if (!levels.length && profile) {
      levels = profile.levels.slice();
    }

    // Volume: prefer metafield, fall back to title extraction
    let volumeNum = null;
    if (product.volume) {
      const v = parseInt(product.volume, 10);
      if (!isNaN(v)) volumeNum = v;
    }
    if (volumeNum === null) {
      volumeNum = extractVolumeFromTitle(product.title);
    }

    const resolvedFamily = String(family || "").trim();
    if (!disciplines.length && resolvedFamily) {
      const famProfile = MODEL_PROFILES[resolvedFamily];
      disciplines = famProfile ? famProfile.disciplines.slice() : ["freeride"];
    }
    if (!levels.length && resolvedFamily) {
      const famProfile = MODEL_PROFILES[resolvedFamily];
      levels = famProfile ? famProfile.levels.slice() : ["intermediate", "advanced"];
    }

    return { disciplines, levels, family, volumeNum };
  }

  /**
   * Compute target volume for a rider weight + skill level.
   * Industry heuristic: beginners need extra volume for stability.
   */
  function targetVolumeForWeight(weightKg, level) {
    const offset = { beginner: 60, intermediate: 30, advanced: 10, expert: 0 }[level] ?? 25;
    return weightKg + offset;
  }

  /**
   * Check whether a product's level set is a graceful match for the user level.
   * Matches the exact level OR one step in either direction (forgiving).
   */
  function matchesLevel(productLevels, userLevel) {
    const userIdx = LEVEL_ORDER.indexOf(userLevel);
    if (userIdx === -1) return true; // unknown user level -> don't exclude
    return productLevels.some((l) => {
      const pIdx = LEVEL_ORDER.indexOf(l);
      if (pIdx === -1) return false;
      return Math.abs(pIdx - userIdx) <= 1;
    });
  }

  // Expose to BoardFinder class
  window.BoardFinderData = {
    LEVEL_ORDER,
    DISCIPLINE_MAP,
    SKILL_MAP,
    MODEL_PROFILES,
    classifyProduct,
    extractFamilyFromTitle,
    extractVolumeFromTitle,
    targetVolumeForWeight,
    matchesLevel,
  };
})();
